import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  Error,
  Close,
  Schedule,
  CreditCard,
} from "@mui/icons-material";
import GenericModal from "../GenericModal";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import {
  disableTostify,
  PAYMENT_TYPES,
  SKIP_PAYMENT_IF_ZERO_AMOUNT,
} from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import styles from "./PaymentStatusModal.module.css";

interface PaymentStatusModalProps {
  open: boolean;
  onClose: () => void;
}

const PaymentStatusModal: React.FC<PaymentStatusModalProps> = ({
  open,
  onClose,
}) => {
  const [paymentStatus, setPaymentStatus] = useState<
    "success" | "error" | "loading"
  >("loading");
  const [message, setMessage] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [totalHours, setTotalHours] = useState<string>("");

  useEffect(() => {
    if (open) {
      checkPaymentStatus();
    }
  }, [open]);

  const checkPaymentStatus = async () => {
    try {
      setLoading(true);
      const storedOrderId = localStorage.getItem("paymentOrderId") || "";
      const supplementaryHours =
        localStorage.getItem("supplementaryHours") || "";

      setTotalHours(supplementaryHours);

      if (!storedOrderId) {
        if (SKIP_PAYMENT_IF_ZERO_AMOUNT) {
          setPaymentStatus("success");
          setMessage(Vocabulary.successfulRenewalMessage);
          await activateSubscription();
        }
        return;
      }

      const response = await postData(
        endpoints.payments.getPaymentStatus + disableTostify,
        { orderId: storedOrderId }
      );

      if (response?.data) {
        const status = response.data.status;
        setMessage(response.data.message);
        setPaymentStatus(status === "success" ? "success" : "error");

        if (status === "success") {
          // Confirm the payment server-side — confirmOnlinePayment runs the
          // paymentType-specific follow-up (crediting hours, activating a
          // subscription, generating the invoice) exactly once.
          await postData(
            endpoints.payments.confirmPayment + disableTostify,
            { transactionId: storedOrderId }
          );
        }
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentStatus("error");
      setMessage(Vocabulary.paymentStatusCheckError);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Activate a subscription for a client.
   * This function first updates the client with the new subscription id,
   * and then sends a request to the backend to activate the subscription.
   * @returns {Promise<void>}
   */
  const activateSubscription = async () => {
    const clientId = localStorage.getItem("processingClientId");
    const subscriptionId = localStorage.getItem("subscriptionId");

    if (clientId && subscriptionId) {
      await postData(endpoints.client.activateSubscription, {
        clientId,
        subscriptionId,
      });
    }
  };

  const handleClose = () => {
    //Clear localStorage on close
    localStorage.removeItem("paymentOrderId");
    localStorage.removeItem("paymentAmount");
    localStorage.removeItem("supplementaryHours");
    localStorage.removeItem("supplementaryHoursOrderId");
    localStorage.removeItem("purchasedHours");
    localStorage.removeItem("bonusHours");
    localStorage.removeItem("productName");
    localStorage.removeItem("processingOrderId");
    localStorage.removeItem("processingClientId");
    localStorage.removeItem("paymentType");
    onClose();
  };

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={Vocabulary.paymentStatus}
      actions={
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleClose}
          startIcon={<Close />}
        >
          {Vocabulary.close}
        </Button>
      }
      width="650px"
    >
      <Box className={styles.modalContent}>
        {loading ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={48} />
            <Typography variant="body1" className={styles.loadingText}>
              {Vocabulary.checkingPaymentStatus}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Status Header */}
            <Box
              className={`${styles.statusHeader} ${
                paymentStatus === "success"
                  ? styles.successHeader
                  : styles.errorHeader
              }`}
            >
              {paymentStatus === "success" ? (
                <CheckCircle className={styles.largeIcon} />
              ) : (
                <Error className={styles.largeIcon} />
              )}
              <Typography variant="h5" className={styles.statusTitle}>
                {message}
              </Typography>
            </Box>

            {localStorage.getItem("paymentType") ===
              PAYMENT_TYPES.SUPPLEMENTARY_HOURS && (
              <>
                {/* Success Details Card */}
                {paymentStatus === "success" && (
                  <Card className={styles.detailsCard}>
                    <Box className={styles.cardContent}>
                      <Box className={styles.detailItem}>
                        <Box className={styles.detailIcon}>
                          <CreditCard />
                        </Box>
                        <Box className={styles.detailText}>
                          <Typography
                            variant="caption"
                            className={styles.label}
                          >
                            {Vocabulary.service}
                          </Typography>
                          <Typography variant="body2" className={styles.value}>
                            {localStorage.getItem("productName") || "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider className={styles.divider} />

                      <Box className={styles.detailItem}>
                        <Box className={styles.detailIcon}>
                          <Schedule />
                        </Box>
                        <Box className={styles.detailText}>
                          <Typography
                            variant="caption"
                            className={styles.label}
                          >
                            {Vocabulary.supplementaryHours}
                          </Typography>
                          <Typography variant="body2" className={styles.value}>
                            {totalHours || "0"} ore
                          </Typography>
                        </Box>
                      </Box>

                      <Divider className={styles.divider} />

                      <Box className={styles.detailItem}>
                        <Typography
                          variant="body2"
                          className={styles.successMessage}
                        >
                          ✓ {Vocabulary.paymentSuppliemntaryHoursSuccessMessage}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </GenericModal>
  );
};

export default PaymentStatusModal;
